import React, { useState } from 'react';
import { useStore } from '../../store';
import { usePermissions } from '../../hooks/usePermissions';
import { X, Trash2, Clock, MessageSquare, Plus, Lock, AlertCircle } from 'lucide-react';
import { STATUS, PRIORITY, TASK_TYPE, fmt } from '../../utils';
import Avatar from '../UI/Avatar';
import AssigneePicker from '../UI/AssigneePicker';
import { tasks as tasksApi } from '../../api';


export default function TaskModal() {
  const { taskModalOpen, activeTask, closeTask, updateTask, deleteTask } = useStore();
  if (!taskModalOpen || !activeTask) return null;

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && closeTask()}>
      <div
        className="modal animate-scale-in w-[calc(100vw-1.5rem)] sm:w-full sm:max-w-3xl lg:max-w-5xl xl:max-w-[46%]"
        onClick={e => e.stopPropagation()}
      >
        <TaskDetail task={activeTask} onClose={closeTask} onUpdate={updateTask} onDelete={deleteTask} />
      </div>
    </div>
  );
}

function TaskDetail({ task, onClose, onUpdate, onDelete }) {
  // --- IGNORE ---
  const { activeProject, createTask, sprints, projects } = useStore();
  console.log('TaskDetail render', { projects });
  const { canEditTask, canDeleteTask, canComment } = usePermissions(task.project);
  const checkboxAccent = activeProject?.color || '#4f63ff';
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || '');
  const [comment, setComment] = useState('');
  const [timeMinutes, setTimeMinutes] = useState('');
  const [timeDesc, setTimeDesc] = useState('');
  const [showTimeLog, setShowTimeLog] = useState(false);
  const [checkText, setCheckText] = useState('');
  const [loadingComment, setLoadingComment] = useState(false);

  const canEdit = canEditTask();
  const canDelete = canDeleteTask();
  const canAddComment = canComment();

  const save = (field, value) => {
    if (!canEdit) {
      alert('У вас нет прав для редактирования задач');
      return;
    }
    onUpdate(task._id, { [field]: value });
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    setLoadingComment(true);
    try {
      await tasksApi.addComment(task._id, { text: comment });
      setComment('');
      // Refresh task directly without using updateTask (which does PATCH)
      const updated = await tasksApi.get(task._id);
      // Update store directly
      useStore.setState(s => ({
        tasks: s.tasks.map(t => t._id === task._id ? updated : t),
        activeTask: s.activeTask?._id === task._id ? updated : s.activeTask
      }));
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setLoadingComment(false);
    }
  };

  const logTime = async () => {
    const mins = parseInt(timeMinutes);
    if (!mins || mins < 1) return;
    await tasksApi.logTime(task._id, { minutes: mins, description: timeDesc });
    setTimeMinutes(''); setTimeDesc('');
    const updated = await tasksApi.get(task._id);
    onUpdate(task._id, updated);
  };

  const addCheckItem = async () => {
    if (!checkText.trim()) return;
    const checklist = [...(task.checklist || []), { text: checkText, done: false }];
    await onUpdate(task._id, { checklist });
    setCheckText('');
  };

  const toggleCheck = async (i) => {
    const checklist = task.checklist.map((c, idx) => idx === i ? { ...c, done: !c.done } : c);
    await onUpdate(task._id, { checklist });
  };

  const totalLogged = (task.timeLogs || []).reduce((s, l) => s + l.minutes, 0);
  const checkDone = (task.checklist || []).filter(c => c.done).length;
  const checkTotal = (task.checklist || []).length;

  return (
    <div className="flex flex-col max-h-[90vh]">
      {/* Header */}
      <div className="flex items-start gap-3 p-5 pb-3 border-b border-gray-100 dark:border-gray-800">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 text-xs text-gray-400">
            <span>{TASK_TYPE[task.type]?.icon} {TASK_TYPE[task.type]?.label}</span>
            {task.number && <span className="font-mono">#{task.number}</span>}
            {task.parent && <span>↳ подзадача</span>}
          </div>
          {editing ? (
            <input className="input text-base font-semibold" value={title}
              onChange={e => setTitle(e.target.value)}
              onBlur={() => { save('title', title); setEditing(false); }}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()}
              autoFocus />
          ) : (
            <h2 className={`text-lg font-semibold ${canEdit ? 'cursor-pointer hover:text-brand-600 transition-colors' : 'text-gray-600'}`}
              onClick={() => canEdit && setEditing(true)}>{task.title}</h2>
          )}
        </div>
        <div className="flex items-center gap-1">
          {canDelete ? (
            <button onClick={() => canDelete && onDelete(task._id)} className="btn-ghost p-1.5 text-red-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
          ) : (
            <button disabled title="Требуется роль администратора" className="btn-ghost p-1.5 text-gray-300 cursor-not-allowed"><Trash2 className="w-4 h-4" /></button>
          )}
          <button onClick={onClose} className="btn-ghost p-1.5"><X className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {!canEdit && (
          <div className="flex gap-3 items-start p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 m-3 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900 dark:text-amber-100 text-sm">Требуется роль участника</p>
              <p className="text-xs text-amber-800 dark:text-amber-200 mt-0.5">Для редактирования задач нужны права участника или выше</p>
            </div>
          </div>
        )}
        <div className="flex flex-col lg:flex-row gap-0 divide-y lg:divide-y-0 lg:divide-x divide-gray-100 dark:divide-gray-800">
          {/* Left: main content */}
          <div className="flex-1 p-5 space-y-5 min-w-0">
            {/* Description */}
            <div>
              <label className="label">Описание</label>
              <textarea className="input resize-none text-sm" rows={3} placeholder="Добавить описание..." disabled={!canEdit}
                value={description}
                onChange={e => setDescription(e.target.value)}
                onBlur={() => save('description', description)} />
            </div>

            {/* Checklist */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label mb-0">Чеклист {checkTotal > 0 && <span className="ml-1 text-gray-400">{checkDone}/{checkTotal}</span>}</label>
              </div>
              {checkTotal > 0 && (
                <div className="h-1 bg-gray-100 dark:bg-gray-800 rounded-full mb-2 overflow-hidden">
                  <div className="h-full bg-brand-500 rounded-full transition-all" style={{ width: `${(checkDone / checkTotal) * 100}%` }} />
                </div>
              )}
              <div className="space-y-1 mb-2">
                {(task.checklist || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2 group">
                    <input type="checkbox" disabled={!canEdit} checked={c.done} onChange={() => canEdit && toggleCheck(i)}
                      className="rounded-checkbox"
                      style={{ '--checkbox-accent': checkboxAccent, '--checkbox-border': checkboxAccent }}
                    />
                    <span className={`text-sm flex-1 ${c.done ? 'line-through text-gray-400' : ''}`}>{c.text}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input className="input text-sm" disabled={!canEdit} placeholder="Новый пункт..." value={checkText}
                  onChange={e => setCheckText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && canEdit && addCheckItem()} />
                <button onClick={addCheckItem} disabled={!canEdit} className="btn-secondary px-2"><Plus className="w-4 h-4" /></button>
              </div>
            </div>

            {/* Subtasks */}
            {task.subtasks?.length > 0 && (
              <div>
                <label className="label">Подзадачи ({task.subtasks.length})</label>
                <div className="space-y-1">
                  {task.subtasks.map(st => (
                    <div key={st._id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                      <span className="text-xs">{TASK_TYPE[st.type]?.icon}</span>
                      <span className={`flex-1 text-sm ${st.status === 'done' ? 'line-through text-gray-400' : ''}`}>{st.title}</span>
                      <span className="w-2 h-2 rounded-full" style={{ background: STATUS[st.status]?.color }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Time tracking */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label mb-0 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />Тайм-трекинг</label>
                {canEdit && (
                  <button onClick={() => setShowTimeLog(p => !p)} className="text-xs text-brand-600 hover:underline">+ Записать время</button>
                )}
              </div>
              {totalLogged > 0 && (
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-500 rounded-full" style={{ width: task.estimatedMinutes ? `${Math.min((totalLogged / task.estimatedMinutes) * 100, 100)}%` : '100%' }} />
                  </div>
                  <span className="text-xs text-gray-500">{fmt.time(totalLogged)}{task.estimatedMinutes ? ` / ${fmt.time(task.estimatedMinutes)}` : ''}</span>
                </div>
              )}
              {showTimeLog && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 space-y-2">
                  <div className="flex gap-2">
                    <input className="input text-sm w-28" type="number" placeholder="Минуты" min="1"
                      value={timeMinutes} onChange={e => setTimeMinutes(e.target.value)} />
                    <input className="input text-sm flex-1" placeholder="Что делали..." value={timeDesc}
                      onChange={e => setTimeDesc(e.target.value)} />
                    <button onClick={logTime} className="btn-primary px-3">OK</button>
                  </div>
                </div>
              )}
              {(task.timeLogs || []).slice(-3).map((log, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-gray-500 py-1">
                  <Avatar name={log.user?.name} color={log.user?.color} size="xs" />
                  <span>{fmt.time(log.minutes)}</span>
                  {log.description && <span className="text-gray-400">— {log.description}</span>}
                  <span className="ml-auto">{fmt.ago(log.loggedAt)}</span>
                </div>
              ))}
            </div>

            {/* Comments */}
            <div>
              <label className="label flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" />Комментарии ({(task.comments || []).length})</label>
              <div className="space-y-3 mb-3">
                {(task.comments || []).map((c, i) => (
                  <div key={i} className="flex gap-2">
                    <Avatar name={c.author?.name} color={c.author?.color} size="sm" />
                    <div className="flex-1 bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium">{c.author?.name}</span>
                        <span className="text-xs text-gray-400">{fmt.ago(c.createdAt)}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{c.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              {canAddComment ? (
                <div className="flex gap-2">
                  <textarea className="input resize-none text-sm flex-1" rows={2} placeholder="Написать комментарий..."
                    value={comment} onChange={e => setComment(e.target.value)} />
                  <button onClick={submitComment} disabled={loadingComment || !comment.trim()} className="btn-primary px-3 self-end">
                    {loadingComment ? '...' : 'OK'}
                  </button>
                </div>
              ) : (
                <p className="text-xs text-gray-400">Для комментирования нужно быть участником проекта</p>
              )}
            </div>
          </div>

          {/* Right: metadata */}
          <div className="w-full lg:w-[30%] flex-shrink-0 p-4 space-y-4">
            <MetaField label="Статус">
              <select className="input text-xs" disabled={!canEdit} value={task.status} onChange={e => save('status', e.target.value)}>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </MetaField>
            <MetaField label="Приоритет">
              <select className="input text-xs" disabled={!canEdit} value={task.priority} onChange={e => save('priority', e.target.value)}>
                {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
              </select>
            </MetaField>
            <MetaField label="Тип">
              <select className="input text-xs" disabled={!canEdit} value={task.type} onChange={e => save('type', e.target.value)}>
                {Object.entries(TASK_TYPE).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
              </select>
            </MetaField>
            <MetaField label="Исполнители">
              <div className="space-y-1">
                <AssigneePicker
                  value={(task.assignees || []).map(a => a._id)}
                  onChange={(ids) => canEdit ? save('assignees', ids) : alert('У вас нет прав для редактирования')}
                  members={ projects.find(item => item._id === task.project._id)?.members || []}
                  size="sm"
                  disabled={!canEdit}
                />
                {(task.assignees || []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {(task.assignees || []).map(a => (
                      <span key={a._id} className="text-[11px] bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-200 px-2 py-0.5 rounded-full">{a.name}</span>
                    ))}
                  </div>
                )}
              </div>
            </MetaField>
            <MetaField label="Срок">
              <input className="input text-xs" disabled={!canEdit} type="date"
                value={task.dueDate ? task.dueDate.slice(0, 10) : ''}
                onChange={e => save('dueDate', e.target.value || null)} />
            </MetaField>
            <MetaField label="Оценка (мин)">
              <input className="input text-xs" disabled={!canEdit} type="number" placeholder="0"
                defaultValue={task.estimatedMinutes || ''}
                onBlur={e => save('estimatedMinutes', parseInt(e.target.value) || 0)} />
            </MetaField>
            {task.reporter && (
              <MetaField label="Автор">
                <div className="flex items-center gap-1.5">
                  <Avatar name={task.reporter.name} color={task.reporter.color} size="xs" />
                  <span className="text-xs text-gray-600 dark:text-gray-400">{task.reporter.name}</span>
                </div>
              </MetaField>
            )}
            <MetaField label="Создано"><span className="text-xs text-gray-500">{fmt.date(task.createdAt)}</span></MetaField>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaField({ label, children }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}
